import { CheckCircle2, Search, Upload, XCircle } from "lucide-react";
import type { KycRecord } from "@/types/kyc";
import { formatDate, formatTime } from "@/pages/admin/data";

interface Entry {
  title: string;
  actor: string;
  ts: string;
  icon: typeof Upload;
  color: string;
  bg: string;
}

export default function ReviewHistory({ record }: { record: KycRecord }) {
  const submittedBy =
    record.tradeName || record.legalBusinessName || record.shopName || record.retailerName || record.retailerId;
  const entries: Entry[] = [];

  if (record.submittedAt) {
    entries.push({
      title: "KYC submitted by retailer",
      actor: submittedBy,
      ts: record.submittedAt,
      icon: Upload,
      color: "text-[#0284C7]",
      bg: "bg-[#F0F9FF]",
    });
  }

  if (record.status === "SUBMITTED" || record.status === "UNDER_REVIEW") {
    entries.push({
      title: "Application picked for review",
      actor: "Admin",
      ts: new Date().toISOString(),
      icon: Search,
      color: "text-[#D97706]",
      bg: "bg-[#FFFBEB]",
    });
  } else if (record.reviewedAt) {
    const approved = record.status === "APPROVED";
    entries.push({
      title: approved ? "KYC approved" : "KYC rejected",
      actor: record.reviewedBy ?? "Admin",
      ts: record.reviewedAt,
      icon: approved ? CheckCircle2 : XCircle,
      color: approved ? "text-[#059669]" : "text-[#DC2626]",
      bg: approved ? "bg-[#ECFDF5]" : "bg-[#FEF2F2]",
    });
  }

  return (
    <div className="rounded-[10px] border border-line bg-white p-5 shadow-card">
      <h3 className="text-lg font-semibold text-ink">Review History</h3>
      <div className="mt-4 flex flex-col">
        {entries.map((e, i) => {
          const Icon = e.icon;
          const last = i === entries.length - 1;
          return (
            <div key={i} className="relative flex gap-3">
              {!last && <span className="absolute left-[19px] top-11 bottom-0 w-px bg-[#F1F5F9]" />}
              <span className={`relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${e.bg} ${e.color}`}>
                <Icon className="h-5 w-5" />
              </span>
              <div className={`${last ? "pb-0" : "pb-6"} pt-0.5`}>
                <p className="text-sm font-medium text-ink">{e.title}</p>
                <p className="mt-0.5 text-xs text-ink-secondary">
                  {e.actor} · {formatDate(e.ts)} {formatTime(e.ts)}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}